import React, { useState } from 'react';
import { createUserWithEmailAndPassword, getAuth } from 'firebase/auth';
import { auth, db, firebaseConfig, initializeSecondaryApp, deleteApp } from '../../config/firebase';
import { collection, addDoc } from 'firebase/firestore';
import { sendCustomPasswordReset } from '../../services/emailService';
import '../../styles/Modal.css';

const BulkUserImportModal = ({ onClose, onComplete, districts }) => {
  const [csvData, setCsvData] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [results, setResults] = useState(null);
  const [sendWelcomeEmails, setSendWelcomeEmails] = useState(true);

  const sampleCSV = `email,displayName,role,districtNames
john.doe@example.com,John Doe,dCOM,"District A,District B"
jane.smith@example.com,Jane Smith,dCOM Member,District C
admin@example.com,Admin User,Admin,`;

  const parseCSV = (csvText) => {
    const lines = csvText.trim().split('\n');
    if (lines.length < 2) {
      throw new Error('CSV must have at least a header row and one data row');
    }

    const headers = lines[0].split(',').map(h => h.trim());
    const requiredHeaders = ['email', 'displayName', 'role'];
    
    for (const required of requiredHeaders) {
      if (!headers.includes(required)) {
        throw new Error(`Missing required column: ${required}`);
      }
    }

    const users = [];
    for (let i = 1; i < lines.length; i++) {
      const values = parseCSVLine(lines[i]);
      if (values.length !== headers.length) {
        throw new Error(`Row ${i + 1} has ${values.length} columns, expected ${headers.length}`);
      }

      const user = {};
      headers.forEach((header, index) => {
        user[header] = values[index].trim();
      });

      // Validate required fields
      if (!user.email || !user.displayName || !user.role) {
        throw new Error(`Row ${i + 1} is missing required fields`);
      }

      // Validate email format
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(user.email)) {
        throw new Error(`Row ${i + 1} has invalid email format: ${user.email}`);
      }

      // Validate role
      const validRoles = ['Admin', 'dCOM', 'dCOM Member', 'Candidate'];
      if (!validRoles.includes(user.role)) {
        throw new Error(`Row ${i + 1} has invalid role: ${user.role}. Valid roles: ${validRoles.join(', ')}`);
      }

      // Parse district names for dCOM roles
      if ((user.role === 'dCOM' || user.role === 'dCOM Member') && user.districtNames) {
        const districtNames = user.districtNames.split(',').map(name => name.trim().replace(/"/g, ''));
        const districtIds = [];
        
        for (const districtName of districtNames) {
          const district = districts.find(d => d.name === districtName);
          if (!district) {
            throw new Error(`Row ${i + 1} references unknown district: ${districtName}`);
          }
          districtIds.push(district.id);
        }
        
        user.districtIds = districtIds;
      } else if (user.role === 'dCOM' || user.role === 'dCOM Member') {
        throw new Error(`Row ${i + 1}: ${user.role} users must have district assignments`);
      }

      users.push(user);
    }

    return users;
  };

  const parseCSVLine = (line) => {
    const result = [];
    let current = '';
    let inQuotes = false;
    
    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      
      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === ',' && !inQuotes) {
        result.push(current);
        current = '';
      } else {
        current += char;
      }
    }
    
    result.push(current);
    return result;
  };

  const handleImport = async () => {
    if (!csvData.trim()) {
      setError('Please enter CSV data');
      return;
    }

    setLoading(true);
    setError('');
    setResults(null);

    try {
      const users = parseCSV(csvData);
      const results = {
        total: users.length,
        successful: [],
        failed: []
      };

      for (const userData of users) {
        try {
          // Generate a temporary password for Firebase Auth creation
          const tempPassword = Math.random().toString(36).slice(-12) + 'A1!';

          // Use a secondary app so creating the Auth account does not
          // switch the active admin session (same fix as single-user creation).
          const secondaryApp = initializeSecondaryApp(firebaseConfig, `bulk-create-${Date.now()}`);
          const secondaryAuth = getAuth(secondaryApp);
          let newUid;
          try {
            const userCredential = await createUserWithEmailAndPassword(
              secondaryAuth,
              userData.email,
              tempPassword
            );
            newUid = userCredential.user.uid;
          } finally {
            await deleteApp(secondaryApp);
          }

          // Add user data to Firestore
          await addDoc(collection(db, 'users'), {
            uid: newUid,
            email: userData.email,
            displayName: userData.displayName,
            role: userData.role,
            districtIds: userData.districtIds || [],
            districtId: userData.districtIds && userData.districtIds.length > 0 ? userData.districtIds[0] : null,
            active: true,
            needsPasswordReset: true,
            createdAt: new Date(),
            createdBy: auth.currentUser?.uid  // auth still holds the admin session
          });

          // Send password reset email
          try {
            await sendCustomPasswordReset(userData.email);
          } catch (resetError) {
            console.error('Error sending password reset email:', resetError);
          }

          results.successful.push({
            email: userData.email,
            displayName: userData.displayName,
            role: userData.role
          });

        } catch (userError) {
          console.error(`Error creating user ${userData.email}:`, userError);
          results.failed.push({
            email: userData.email,
            displayName: userData.displayName,
            error: userError.message
          });
        }
      }

      setResults(results);
      
      if (results.successful.length > 0) {
        onComplete();
      }

    } catch (error) {
      console.error('Error parsing CSV:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const downloadSample = () => {
    const blob = new Blob([sampleCSV], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'sample-users.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content" style={{ maxWidth: '800px', maxHeight: '90vh', overflow: 'auto' }}>
        <div className="modal-header">
          <h2>Bulk Import Users</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <div className="modal-form">
          {error && <div className="error-message">{error}</div>}

          {!results && (
            <>
              <div className="form-info">
                <h3>CSV Format Instructions</h3>
                <p>Upload a CSV file with the following columns:</p>
                <ul>
                  <li><strong>email</strong> - User's email address (required)</li>
                  <li><strong>displayName</strong> - User's display name (required)</li>
                  <li><strong>role</strong> - User role: Admin, dCOM, dCOM Member, or Candidate (required)</li>
                  <li><strong>districtNames</strong> - Comma-separated district names for dCOM roles (required for dCOM/dCOM Member)</li>
                </ul>
                
                <button 
                  type="button" 
                  className="btn btn-secondary"
                  onClick={downloadSample}
                  style={{ marginTop: '10px' }}
                >
                  Download Sample CSV
                </button>
              </div>

              <div className="form-group">
                <label htmlFor="csvData">CSV Data:</label>
                <textarea
                  id="csvData"
                  value={csvData}
                  onChange={(e) => setCsvData(e.target.value)}
                  placeholder="Paste your CSV data here or type it manually..."
                  rows="10"
                  disabled={loading}
                  style={{ fontFamily: 'monospace', fontSize: '12px' }}
                />
              </div>

              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={sendWelcomeEmails}
                    onChange={(e) => setSendWelcomeEmails(e.target.checked)}
                    disabled={loading}
                  />
                  <span className="checkbox-text">Send password reset emails to all users</span>
                </label>
                <small className="form-help">
                  Users will receive an email with instructions to set their password.
                </small>
              </div>
            </>
          )}

          {results && (
            <div className="import-results">
              <h3>Import Results</h3>
              <div className="results-summary">
                <p><strong>Total Users:</strong> {results.total}</p>
                <p style={{ color: 'green' }}><strong>Successful:</strong> {results.successful.length}</p>
                <p style={{ color: 'red' }}><strong>Failed:</strong> {results.failed.length}</p>
              </div>

              {results.successful.length > 0 && (
                <div className="successful-users">
                  <h4>Successfully Created Users:</h4>
                  <ul>
                    {results.successful.map((user, index) => (
                      <li key={index}>
                        {user.displayName} ({user.email}) - {user.role}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {results.failed.length > 0 && (
                <div className="failed-users">
                  <h4>Failed to Create:</h4>
                  <ul>
                    {results.failed.map((user, index) => (
                      <li key={index} style={{ color: 'red' }}>
                        {user.displayName} ({user.email}) - {user.error}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          <div className="modal-actions">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onClose}
              disabled={loading}
            >
              {results ? 'Close' : 'Cancel'}
            </button>
            {!results && (
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleImport}
                disabled={loading || !csvData.trim()}
              >
                {loading ? 'Importing...' : 'Import Users'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BulkUserImportModal;
